import { Row, Skeleton } from 'antd';
import { debounce } from 'lodash';
import { useEffect, useMemo, useState } from 'react';

import {
  Descriptions,
  Form,
  FormatItem,
  FormItemSelect,
  Modal,
  Title,
} from '../../../../../../components/index';
import { TableFormList } from '../../../../../../components/common/tableFormList/TableFormList';
import { useGetCargoTypeQuery } from '../../../../../../services/cargoApi';
import { useGetCompaniesQuery } from '../../../../../../services/companyApi';
import { useGetMerchantsCeGroupbyNcmItemsQuery } from '../../../../../../services/ncmItemApi';
import { usePatchAllOperatorManifestItemsMutation } from '../../../../../../services/stopoverApi';
import {
  CeMerchantGroupbyNcmItemType,
  NCMItemType,
} from '../../../../../../types';
import { removeSpecialCharacters } from '../../../../../../utils/utils';
import { SelectAllItemsOperatorModalContainer } from './styles';

const { Item } = Descriptions;

function ItemLoading(props: {
  index: number;
  itemRowProps?: { isVehicleManifest: boolean };
}) {
  const { index, itemRowProps } = props;
  const { isVehicleManifest } = itemRowProps || { isVehicleManifest: false };

  const [searchStorageName, setSearchStorageName] = useState('');
  const { data: storagesData, isLoading: isLoadingStoragesData } =
    useGetCompaniesQuery({
      company_type: 'STORAGE',
      name_or_cnpj: searchStorageName,
      page_size: 15,
    });

  const [searchOperatorName, setSearchOperatorName] = useState('');
  const { data: operatorsData, isLoading: isLoadingOperatorsData } =
    useGetCompaniesQuery({
      company_type: 'OPERATOR',
      name_or_cnpj: searchOperatorName,
      page_size: 15,
    });

  const [searchCargoName, setSearchCargoName] = useState('');
  const { data: cargoTypesData, isLoading: isLoadingCargoTypesData } =
    useGetCargoTypeQuery({ name: searchCargoName, page_size: 15 });

  function onSearchStorage(evt: string) {
    const search = removeSpecialCharacters(evt, /[./-]/g);
    setSearchStorageName(search || evt);
  }

  function onSearchOperator(evt: string) {
    const search = removeSpecialCharacters(evt, /[./-]/g);
    setSearchOperatorName(search || evt);
  }

  function onSearchCargo(evt: string) {
    const search = removeSpecialCharacters(evt, /[./-]/g);
    setSearchCargoName(search || evt);
  }

  return (
    <Row
      gutter={16}
      style={{
        background: '#fff',
        margin: '8px 16px 8px 16px',
        padding: '5px',
      }}
    >
      <FormItemSelect
        colSpan={8}
        name={[index, 'cargo_type', 'name']}
        dataList={cargoTypesData?.results
          .slice()
          .sort((a, b) => a.name.localeCompare(b.name))}
        required
        showSearch
        // Em manifesto de veículos o tipo é sempre VEÍCULOS (fixo);
        // para carga geral o usuário escolhe o tipo aplicado ao lote.
        disabled={isVehicleManifest}
        isLoading={isLoadingCargoTypesData}
        onSearch={debounce(onSearchCargo, 300)}
      />
      <FormItemSelect
        colSpan={8}
        name={[index, 'storage_terminal', 'id']}
        dataList={storagesData?.results}
        required
        showSearch
        isLoading={isLoadingStoragesData}
        onSearch={debounce(onSearchStorage, 300)}
      />
      <FormItemSelect
        colSpan={8}
        name={[index, 'operator', 'id']}
        dataList={operatorsData?.results
          .slice()
          .sort((a, b) => a.name.localeCompare(b.name))}
        required
        showSearch
        isLoading={isLoadingOperatorsData}
        onSearch={debounce(onSearchOperator, 300)}
      />
    </Row>
  );
}

type SelectAllItemsOperatorModalProps = {
  isVisible: boolean;
  handleCancelModal: () => void;
  countItems: number;
  selectedManifest: string;
  stopoverId: number;
  selectedOpDirectionManifest: string;
  // Quando informado, o modal atribui somente ao lote de itens selecionados
  // (por ncm_item_id) ao invés de todas as cargas do manifesto.
  ncmItemIds?: number[];
  // Registros do lote selecionado, usados para resumir CE Mercante/consignatário
  // da própria seleção (que pode estar espalhada por várias páginas).
  selectedItems?: CeMerchantGroupbyNcmItemType[];
  // Quantidade de cargas ainda não atribuídas (afetadas no modo "todos/restantes").
  remainingCount?: number;
  // Veículos usam tipo de carga fixo (VEÍCULOS); demais cargas escolhem o tipo.
  isVehicleManifest: boolean;
  // Disparado apenas quando a atribuição é concluída com sucesso.
  onSuccess?: () => void;
};

export function SelectAllItemsOperatorModal(
  props: SelectAllItemsOperatorModalProps
) {
  const {
    isVisible,
    handleCancelModal,
    countItems,
    selectedManifest,
    stopoverId,
    selectedOpDirectionManifest,
    ncmItemIds,
    selectedItems,
    remainingCount,
    isVehicleManifest,
    onSuccess,
  } = props;
  const [form] = Form.useForm();

  const isSelectedMode = Array.isArray(ncmItemIds);
  const displayCount = isSelectedMode
    ? ncmItemIds!.length
    : remainingCount ?? countItems;

  const { data: ncmItemsData, isFetching: isFetchingNcmItemsData } =
    useGetMerchantsCeGroupbyNcmItemsQuery(
      {
        page_size: countItems,
        manifest_code: selectedManifest,
        stopover_id: stopoverId,
      },
      // No modo "selecionados" as cargas do lote podem estar espalhadas por
      // várias páginas, então não montamos o resumo por CE Mercante/consignatário.
      { skip: !isVisible || isSelectedMode }
    );

  useEffect(() => {
    if (isVisible) {
      form.setFieldsValue({
        loadings: [
          {
            cargo_type: isVehicleManifest ? { name: 'VEÍCULOS' } : null,
            storage: null,
            operator: null,
          },
        ],
      });
    }
  }, [isVisible]);

  const [
    patchAllOperatorManifestItems,
    { isLoading: isLoadingSelectAllOperatorManifest },
  ] = usePatchAllOperatorManifestItemsMutation();

  // No modo "selecionados" resumimos a própria seleção; no modo "todos/restantes"
  // resumimos as cargas do manifesto retornadas pela query.
  const summarySource = isSelectedMode
    ? selectedItems ?? []
    : ncmItemsData?.results ?? [];

  const allCeMerchant = useMemo(() => {
    const ceMerchantCodeList: string[] = [];
    summarySource.forEach((item) => {
      if (!ceMerchantCodeList.includes(item.code)) {
        ceMerchantCodeList.push(item.code);
      }
    });
    return ceMerchantCodeList.join(', ');
  }, [summarySource]);

  const allConsignees = useMemo(() => {
    const consigneeList: string[] = [];
    summarySource.forEach((item) => {
      const cnpj = item.operator_company?.cnpj || 'N/A';
      const name = item.operator_company?.name || 'N/A';
      const consignee = `${cnpj} - ${name}`;
      if (!consigneeList.includes(consignee)) {
        consigneeList.push(consignee);
      }
    });
    return consigneeList.join(', ');
  }, [summarySource]);

  const tableFormListColumns = [
    { title: 'TIPO DE MERCADORIA', colSpan: 8 },
    { title: 'LOCAL DE ARMAZENAMENTO', colSpan: 8 },
    { title: 'OPERADOR', colSpan: 8 },
  ];

  async function handleFormSubmit() {
    form.validateFields().then(async (values: { loadings: NCMItemType[] }) => {
      const item = values.loadings[0];

      const obj = {
        id: stopoverId,
        manifest_code: selectedManifest,
        operator: item.operator?.id || 0,
        storage_terminal: item.storage_terminal?.id || 0,
        cargo_type: item.cargo_type?.name || '',
        ...(isSelectedMode ? { ncm_item_ids: ncmItemIds } : {}),
      };
      const response = await patchAllOperatorManifestItems(obj);
      if ('data' in response) {
        onSuccess?.();
        handleCancelModalSelectOperator();
      }
    });
  }

  function handleCancelModalSelectOperator() {
    form.resetFields();
    handleCancelModal();
  }

  return (
    <Modal
      className="TOSPrimaryModal"
      width={1080}
      title={
        isSelectedMode
          ? 'Selecionar operador(es) para as cargas selecionadas'
          : 'Selecionar operador(es) para as cargas restantes do manifesto'
      }
      visible={isVisible}
      destroyOnClose
      onCancel={handleCancelModalSelectOperator}
      cancelText="Voltar"
      cancelButtonProps={{ disabled: isLoadingSelectAllOperatorManifest }}
      okText="Concluir"
      onOk={handleFormSubmit}
      okButtonProps={{
        style: { marginRight: '16px' },
        loading: isLoadingSelectAllOperatorManifest,
      }}
      paddingBody="0"
    >
      <SelectAllItemsOperatorModalContainer>
        <div className="ncm-description">
          <Title>CARGA</Title>
          <Descriptions column={2}>
            <Item label="Manifesto">
              <FormatItem>{selectedManifest}</FormatItem>
            </Item>
            <Item
              label={(() => {
                // "Unidade" só faz sentido para veículos (cada linha é um chassi).
                // Para as demais cargas cada linha é uma "carga" (CE Mercante x NCM).
                const noun = isVehicleManifest ? 'Unidades' : 'Cargas';
                return isSelectedMode
                  ? `${noun} selecionadas`
                  : `${noun} a atribuir`;
              })()}
            >
              <FormatItem>{displayCount}</FormatItem>
            </Item>
            <Item label="CE Mercante(s)" span={2}>
              {isFetchingNcmItemsData ? (
                <Skeleton.Input
                  active
                  style={{ width: '600px', maxWidth: '100%' }}
                />
              ) : (
                <FormatItem>{allCeMerchant}</FormatItem>
              )}
            </Item>
            <Item
              label={
                selectedOpDirectionManifest === 'LOADING'
                  ? 'Embarcador'
                  : 'Consignatário'
              }
              span={2}
            >
              {isFetchingNcmItemsData ? (
                <Skeleton.Input
                  active
                  style={{ width: '600px', maxWidth: '100%' }}
                />
              ) : (
                <FormatItem>{allConsignees}</FormatItem>
              )}
            </Item>
          </Descriptions>
        </div>
        <div className="select-operator-info">
          <span style={{ lineHeight: '28px' }}>
            Indique o local de armazenamento e o operador.
          </span>
          <Form form={form} name="selectLoading" onFinish={handleFormSubmit}>
            <TableFormList
              formListName="loadings"
              FormListItemRow={ItemLoading}
              columns={tableFormListColumns}
              style={{ marginTop: '0px', marginBottom: '20px' }}
              itemRowProps={{ isVehicleManifest }}
            />
          </Form>
        </div>
      </SelectAllItemsOperatorModalContainer>
    </Modal>
  );
}
